import styled from '@emotion/styled'
import { Arrow } from 'app/components/Common/Arrow'
import { Close } from 'app/components/Common/Close'
import { Gallery } from 'app/components/Common/Gallery'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { Expand } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { easeOutQuint } from 'app/utils/customFunctions'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import { useKeenSlider } from 'keen-slider/react'
import { uniq } from 'lodash'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  className?: string
  images: ImageProps[]
  languageCode: string
  title?: string
}

export const Preview = memo(function Preview({
  className,
  images,
  languageCode,
  title,
}: Props) {
  if (!images) {
    return null
  }

  if (images.length < 1) {
    return null
  }

  const [ref, isVisible] = useInView({
    threshold: 0,
    triggerOnce: true,
  })

  const modalRef = useRef<any>()

  const [_locked, setLocked] = useLockedBody()
  const [clickedSlide, setClickedSlide] = useState<any>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [modalStatus, setModalStatus] = useState(false)

  const [sliderRef, instanceRef] = useKeenSlider({
    defaultAnimation: {
      duration: 1400,
      easing: easeOutQuint,
    },
    loop: images.length > 1 ? true : false,
    slides: {
      perView: 'auto',
      spacing: 48,
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
    },
    breakpoints: {
      '(max-width: 1199px)': {
        slides: {
          perView: 'auto',
          spacing: 16,
        },
      },
    },
  })

  const handleClick = () => {
    setLocked(true)
    setModalStatus(true)

    setClickedSlide(currentSlide)
    setTimeout(() => {
      setClickedSlide(null)
    }, 100)
  }

  useEffect(() => {
    if (modalStatus) {
      document.addEventListener('keydown', function (e) {
        switch (e.key) {
          case 'Escape':
            setLocked(false)
            setModalStatus(false)
            setClickedSlide(0)
            break
        }
      })
    }

    document.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'ArrowRight':
          instanceRef.current?.next()
          break
        case 'ArrowLeft':
          instanceRef.current?.prev()
          break
      }
    })
  }, [instanceRef, modalStatus])

  return (
    <>
      <Container className={className} ref={ref} tabIndex={0}>
        <Carousel className="grab-cursor" ref={sliderRef}>
          {uniq(images).map((item, index) => (
            <Slide key={index} className="keen-slider__slide">
              <Inner className={isVisible ? 'visible' : undefined}>
                <Image {...item} />
              </Inner>
            </Slide>
          ))}
        </Carousel>

        <Media greaterThanOrEqual="desktopSmall">
          {(className: any, renderChildren: any) => {
            return renderChildren ? (
              <ShowGallery
                className={className}
                dial={4}
                onClick={handleClick}
                onKeyDown={(e: any) =>
                  e.key === 'Enter' ? handleClick() : null
                }
                row
                tabIndex={0}
              >
                <Expand />
                {useVocabularyData('expand-gallery', languageCode)}
              </ShowGallery>
            ) : null
          }}
        </Media>

        {images.length > 1 ? (
          <Media greaterThanOrEqual="desktopSmall">
            {(className: any, renderChildren: any) => {
              return renderChildren ? (
                <Arrows
                  className={`${className}${isVisible ? ' visible' : ''}`}
                >
                  <Arrow
                    direction="R"
                    onClick={(e) =>
                      e.stopPropagation() || instanceRef.current?.next()
                    }
                  />

                  <Arrow
                    onClick={(e) =>
                      e.stopPropagation() || instanceRef.current?.prev()
                    }
                  />
                </Arrows>
              ) : null
            }}
          </Media>
        ) : null}
      </Container>

      <Media greaterThanOrEqual="desktopSmall">
        <Modal
          aria-hidden={!modalStatus}
          aria-labelledby="Gallery"
          className={modalStatus ? 'open' : undefined}
          ref={modalRef}
          role="dialog"
        >
          <Close
            languageCode={languageCode}
            onClick={() => {
              setLocked(false)
              setModalStatus(false)
              setClickedSlide(0)
            }}
          />

          {images && images.length > 0 ? (
            <Gallery
              category={title}
              clickedSlide={clickedSlide}
              images={images}
            />
          ) : null}
        </Modal>
      </Media>
    </>
  )
})

const Container = styled.div`
  padding-left: 5vw;
  position: relative;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
    padding-left: 0;
  }
`

const Carousel = styled.div`
  height: 100%;
  min-height: 60vh;
  display: flex;
  outline: none;
  overflow: hidden;

  @media (max-width: 1199px) {
    width: 100vw;
    height: auto;
    min-height: auto;
    padding-left: 2rem;
    overflow: visible;
  }
`

const Slide = styled.div`
  max-width: 58.889vw;
  min-width: 58.889vw;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1.56;
    max-width: calc(100vw - 4rem);
    min-width: calc(100vw - 4rem);
  }
`

const Inner = styled.div`
  height: 100%;
  clip-path: inset(0 0 0 100%);
  &.visible {
    clip-path: inset(0);
    transition: 1s 0.3s ease-in-out;
  }

  @media (max-width: 1199px) {
    clip-path: inset(0);
  }
`

const Arrows = styled.div`
  display: flex;
  flex-direction: column;
  opacity: 0;
  position: absolute;
  top: 50%;
  right: 2vw;
  transform: translateY(-50%);
  transition: 1s 0.6s ease-in-out;
  &.visible {
    opacity: 1;
    right: 5vw;
  }

  > div {
    margin: 0.375rem 0;
  }
`

const ShowGallery = styled(FlexBox)`
  ${styles.description}

  font-size: 0.875rem;
  line-height: 1.25rem;
  cursor: pointer;
  position: absolute;
  bottom: -4.375rem;
  right: 5vw;
  transition: 0.2s ease-in-out;
  &:hover {
    opacity: 0.4;
  }

  svg {
    width: 1rem;
    height: 1rem;
    fill: ${({ theme }) => theme.colors.variants.primaryDark};
    margin-right: 1rem;
    transform: translateY(-0.125rem);
  }
`

const Modal = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight1};
  opacity: 0;
  overflow-y: auto;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.2s ease-out;
  -webkit-overflow-scrolling: auto;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 10002;
  }
`
